import React, { useState, useMemo } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { FaTimes as X } from 'react-icons/fa'
import { Zone, Device, Port } from '@/types/devices'
import { useLayoutStore } from '@/stores/layoutStore'
import { SIGNAL_TYPE_COLORS } from '@/constants/colors'
import type { ZoneConnectionPoint } from '@busswerks/shared'

/**
 * Build the `zone.connectionPoints` array from a set of selected
 * `${deviceId}:${portId}` keys and the devices currently in the zone.
 * Exposed inputs land on the left edge and exposed outputs on the
 * right edge, evenly distributed along the edge — users will be
 * able to reposition them in a later revision.
 */
function buildConnectionPoints(
  exposedKeys: Set<string>,
  zoneDevices: Device[],
): ZoneConnectionPoint[] {
  const inputs: Array<{ device: Device; port: Port }> = []
  const outputs: Array<{ device: Device; port: Port }> = []

  for (const device of zoneDevices) {
    for (const port of device.inputs) {
      if (exposedKeys.has(`${device.id}:${port.id}`)) {
        inputs.push({ device, port })
      }
    }
    for (const port of device.outputs) {
      if (exposedKeys.has(`${device.id}:${port.id}`)) {
        outputs.push({ device, port })
      }
    }
  }

  const points: ZoneConnectionPoint[] = []
  const distribute = (
    items: Array<{ device: Device; port: Port }>,
    edge: 'left' | 'right',
    direction: 'input' | 'output',
  ) => {
    items.forEach(({ device, port }, i) => {
      points.push({
        id: `cp-${device.id}-${port.id}`,
        name: `${device.name} • ${port.name}`,
        edge,
        direction,
        signalType: port.signalType,
        internalDeviceId: device.id,
        internalPortId: port.id,
        position: items.length <= 1 ? 0.5 : (i + 0.5) / items.length,
      })
    })
  }
  distribute(inputs, 'left', 'input')
  distribute(outputs, 'right', 'output')
  return points
}

interface ManageZonesDialogProps {
  isOpen: boolean
  onClose: () => void
  editingZone?: Zone | null
}

const ZONE_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
]

export const ManageZonesDialog: React.FC<ManageZonesDialogProps> = ({
  isOpen,
  onClose,
  editingZone = null,
}) => {
  const devices = useLayoutStore(s => s.devices)
  const createZone = useLayoutStore(s => s.createZone)
  const updateZone = useLayoutStore(s => s.updateZone)
  const updateDevice = useLayoutStore(s => s.updateDevice)

  // Form state
  const [name, setName] = useState(editingZone?.name || '')
  const [description, setDescription] = useState(editingZone?.description || '')
  const [color, setColor] = useState(editingZone?.color || ZONE_COLORS[0])
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(
    new Set(editingZone?.deviceIds || []),
  )
  const [exposedPortKeys, setExposedPortKeys] = useState<Set<string>>(
    new Set(
      (editingZone?.connectionPoints ?? []).map(
        cp => `${cp.internalDeviceId}:${cp.internalPortId}`,
      ),
    ),
  )

  const togglePort = (deviceId: string, portId: string) => {
    const key = `${deviceId}:${portId}`
    setExposedPortKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Devices currently picked for this zone — the Boundary Ports
  // section only lists ports from these devices.
  const selectedDevices = useMemo(
    () => devices.filter(d => selectedDeviceIds.has(d.id)),
    [devices, selectedDeviceIds],
  )

  // Get device assignments to other zones
  const deviceAssignments = useMemo(() => {
    const assignments: Record<string, string | null> = {}
    devices.forEach(device => {
      assignments[device.id] = device.zoneId || null
    })
    return assignments
  }, [devices])

  const toggleDevice = (deviceId: string) => {
    setSelectedDeviceIds(prev => {
      const next = new Set(prev)
      if (next.has(deviceId)) {
        next.delete(deviceId)
      } else {
        next.add(deviceId)
      }
      return next
    })
  }

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a zone name')
      return
    }

    const connectionPoints = buildConnectionPoints(
      exposedPortKeys,
      selectedDevices,
    )

    if (editingZone) {
      // Update existing zone
      const updatedZone: Zone = {
        ...editingZone,
        name: name.trim(),
        description: description.trim() || undefined,
        color,
        deviceIds: Array.from(selectedDeviceIds),
        connectionPoints,
        updatedAt: new Date().toISOString(),
      }
      updateZone(updatedZone)

      // Update device assignments. Removing a device from this zone
      // returns it to the unassigned state (zoneId: undefined), which
      // is rendered at the layout level rather than inside any zone.
      devices.forEach(device => {
        const wasInZone = editingZone.deviceIds.includes(device.id)
        const isInZone = selectedDeviceIds.has(device.id)

        if (wasInZone && !isInZone) {
          updateDevice({ ...device, zoneId: undefined })
        } else if (!wasInZone && isInZone) {
          updateDevice({ ...device, zoneId: editingZone.id })
        }
      })
    } else {
      // Create new zone
      const newZone: Zone = {
        id: `zone-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: name.trim(),
        description: description.trim() || undefined,
        color,
        position: { x: 50, y: 50 },
        size: { width: 600, height: 400 },
        collapsed: false,
        deviceIds: Array.from(selectedDeviceIds),
        connectionPoints,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      createZone(newZone)

      // Assign devices to the zone
      selectedDeviceIds.forEach(deviceId => {
        const device = devices.find(d => d.id === deviceId)
        if (device) {
          updateDevice({ ...device, zoneId: newZone.id })
        }
      })
    }

    onClose()
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset form on close
      setName(editingZone?.name || '')
      setDescription(editingZone?.description || '')
      setColor(editingZone?.color || ZONE_COLORS[0])
      setSelectedDeviceIds(new Set(editingZone?.deviceIds || []))
      setExposedPortKeys(
        new Set(
          (editingZone?.connectionPoints ?? []).map(
            cp => `${cp.internalDeviceId}:${cp.internalPortId}`,
          ),
        ),
      )
      onClose()
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md max-h-[90vh] bg-surface-raised rounded-xl shadow-high z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-brd-subtle">
            <Dialog.Title className="text-lg font-semibold text-content-primary">
              {editingZone ? 'Edit Zone' : 'Create Zone'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-content-secondary hover:text-content-primary transition-colors duration-150">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Zone Name */}
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Zone Name
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-brd-subtle bg-surface-raised text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50"
                placeholder="e.g., Rack A, Synth Section"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Description (optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-brd-subtle bg-surface-raised text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50"
                placeholder="e.g., Main audio interface rack"
              />
            </div>

            {/* Color Picker */}
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">
                Zone Color
              </label>
              <div className="flex gap-2 flex-wrap">
                {ZONE_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-lg transition-all border-2 ${
                      color === c
                        ? 'border-content-primary scale-110'
                        : 'border-brd-subtle hover:scale-105'
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
            </div>

            {/* Boundary Ports */}
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">
                Boundary Ports
              </label>
              {selectedDevices.length === 0 ? (
                <div className="text-xs text-content-tertiary italic">
                  Assign at least one device below to expose its ports on the
                  zone boundary.
                </div>
              ) : (
                <div className="border border-brd-subtle rounded-lg overflow-hidden max-h-48 overflow-y-auto divide-y divide-brd-subtle">
                  {selectedDevices.map(device => {
                    const ports: Array<{
                      port: Port
                      direction: 'in' | 'out'
                    }> = [
                      ...device.inputs.map(p => ({
                        port: p,
                        direction: 'in' as const,
                      })),
                      ...device.outputs.map(p => ({
                        port: p,
                        direction: 'out' as const,
                      })),
                    ]
                    if (ports.length === 0) return null
                    return (
                      <div key={device.id} className="px-3 py-2">
                        <div className="text-xs font-semibold text-content-primary mb-1.5">
                          {device.name}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {ports.map(({ port, direction }) => {
                            const key = `${device.id}:${port.id}`
                            const exposed = exposedPortKeys.has(key)
                            const color =
                              SIGNAL_TYPE_COLORS[port.signalType] ?? '#999'
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => togglePort(device.id, port.id)}
                                className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors duration-150 flex items-center gap-1 ${
                                  exposed
                                    ? 'text-white shadow'
                                    : 'text-content-secondary opacity-60 hover:opacity-100'
                                }`}
                                style={{
                                  borderColor: color,
                                  backgroundColor: exposed
                                    ? color
                                    : 'transparent',
                                }}
                                title={
                                  exposed
                                    ? `Exposed at zone boundary`
                                    : `Click to expose ${port.name}`
                                }
                              >
                                <span className="uppercase text-[8px] font-semibold tracking-wider">
                                  {direction}
                                </span>
                                <span>{port.name}</span>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {exposedPortKeys.size > 0 && (
                <p className="text-[11px] text-content-tertiary mt-1.5">
                  {exposedPortKeys.size} port
                  {exposedPortKeys.size === 1 ? '' : 's'} exposed at the zone
                  boundary
                </p>
              )}
            </div>

            {/* Device Assignment */}
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">
                Assign Devices
              </label>
              <div className="border border-brd-subtle rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                {devices.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-content-tertiary">
                    No devices available. Add devices first.
                  </div>
                ) : (
                  devices.map(device => {
                    const isSelected = selectedDeviceIds.has(device.id)
                    const assignedZoneId = deviceAssignments[device.id]
                    // An unassigned device (no zoneId) is freely
                    // available; a device in another real zone is
                    // locked until removed from that zone first.
                    const isAssignedElsewhere =
                      assignedZoneId && assignedZoneId !== editingZone?.id

                    return (
                      <label
                        key={device.id}
                        className={`flex items-center gap-3 px-3 py-2 border-b border-brd-subtle last:border-b-0 cursor-pointer hover:bg-surface-sunken transition-colors duration-150 ${
                          isAssignedElsewhere && !isSelected
                            ? 'opacity-60 bg-surface-sunken'
                            : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleDevice(device.id)}
                          className="rounded"
                          disabled={Boolean(isAssignedElsewhere && !isSelected)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-content-primary truncate">
                            {device.name}
                          </div>
                          {device.manufacturer && (
                            <div className="text-xs text-content-tertiary truncate">
                              {device.manufacturer}
                            </div>
                          )}
                        </div>
                        {isAssignedElsewhere && !isSelected && (
                          <span className="text-xs text-content-tertiary whitespace-nowrap">
                            (other zone)
                          </span>
                        )}
                      </label>
                    )
                  })
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-brd-subtle">
            <Dialog.Close asChild>
              <button className="px-4 py-2 rounded-lg border border-brd-subtle text-content-primary hover:bg-accent-muted transition-colors duration-150">
                Cancel
              </button>
            </Dialog.Close>
            <button
              onClick={handleSave}
              className="px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent-hover transition-colors duration-150 font-medium"
            >
              {editingZone ? 'Save Zone' : 'Create Zone'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
